import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import {
	Container,
	Stack,
	AppBar,
	Toolbar,
	Typography,
	IconButton,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Dialog,
	Divider
} from '@mui/material';
import { isEmpty } from 'lodash';

import { useParams } from 'react-router-dom';
import { DataContext } from '../../../context/DataContext';

import { getRol, getToken, getUser } from '../../../api/Account';
import ReactToPrint from 'react-to-print';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import NoData from '../../../components/NoData';
import { getBosqueMoraAsync, getSaldoCarteraByGestorAsync } from '../../../api/Report';
import { PrintReport } from '../../../components/PrintReport';
import moment from 'moment';
import {
	getGestoresAsync,
	getGestoresBySucursalAccessAsync,
	getSucursalByUserAsync
} from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx/xlsx.mjs';

const BosqueMora = () => {
	const [data, setData] = useState([]);

	const [sucursalName, setSucursalName] = useState('');
	const [sucursalList, setSucursalList] = useState([]);

	const [gestorList, setGestorList] = useState([]);
	const [gestorName, setGestorName] = useState('');

	const compRef = useRef();

	const { selectedSucursal, selectedGestor, moneda, tipoCredito } = useParams();

	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const isDolar = parseInt(moneda) === 2;

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const formatMoney = (v) =>
		new Intl.NumberFormat(isDolar ? 'en-US' : 'es-NI', {
			style: 'currency',
			currency: isDolar ? 'USD' : 'NIO'
		}).format(v);

	const requestPayload = useMemo(
		() => ({
			moneda,
			gestorId: selectedGestor === 't' ? 0 : Number(selectedGestor),
			sucursalId: selectedSucursal === 't' ? 0 : Number(selectedSucursal),
			tipoCredito: tipoCredito === 't' ? 0 : Number(tipoCredito)
		}),
		[moneda, selectedGestor, selectedSucursal, tipoCredito]
	);

	const fetchData = useCallback(async () => {
		let sucursals = await getSucursals();

		if (selectedSucursal === 't') {
			setSucursalName('Todas');
		} else {
			const suc = sucursals.find((s) => s.id.toString() === selectedSucursal);
			setSucursalName(suc?.description ?? '');
		}

		setIsLoading(true);

		const result = await getBosqueMoraAsync(token, requestPayload);

		setIsLoading(false);

		if (!result.statusResponse) {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setData(result.data.result);
	}, [requestPayload, selectedSucursal, token, setIsLoading, setIsUnautorized]);

	const getSucursals = async () => {
		setIsLoading(true);
		const result = await getSucursalByUserAsync(token);

		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}
		setIsLoading(false);
		setSucursalList(
			selectedSucursal === 't'
				? result.data.result
				: result.data.result.filter((g) => g.id.toString() === selectedSucursal)
		);

		let gestores = await getGestores(selectedSucursal);

		if (selectedGestor === 't') {
			setGestorName('Todos');
		} else {
			let name = gestores.filter((item) => item.id.toString() === selectedGestor);

			setGestorName(name[0].fullName);
		}
		return result.data.result;
	};

	const getGestores = async (sucursal) => {
		setIsLoading(true);
		const result =
			sucursal === 't'
				? await getGestoresBySucursalAccessAsync(token)
				: await getGestoresAsync(token, sucursal);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setIsLoading(false);
		setGestorList(result.data.result);
		return result.data.result;
	};

	const hasFetchedRef = useRef(false);

	useEffect(() => {
		if (hasFetchedRef.current) return;
		hasFetchedRef.current = true;

		fetchData();
	}, [fetchData]);

	const pageStyle = `
    @media print {
      html, body {
        height: initial !important;
        overflow: initial !important;
        -webkit-print-color-adjust: exact;
      }
    }
    @media all {
      .pagebreak {
        display: none;
      }
    }
    @media print {
      .pagebreak {
        page-break-before: always;
      }
    }  
    @page { size: landscape;  margin: 3mm; }
  `;

	const exportExcel = () => {
		let exp = [];
		data.map((item) => {
			const { avanzada, gestor, riesgo, riesgoPerdido, temprana, vencido } = item;

			let obj = {
				gestor,
				temprana,
				avanzada,
				riesgo,
				riesgoPerdido,
				vencido
			};
			exp.push(obj);
		});

		var bk = XLSX.utils.book_new(),
			bs = XLSX.utils.json_to_sheet(exp);

		XLSX.utils.book_append_sheet(bk, bs, 'bosqueMora');
		XLSX.writeFile(bk, 'BosqueMora.xlsx');
	};

	const paramText = (
		<Container maxWidth={false} sx={{ textAlign: 'center' }}>
			<Stack direction={'row'} justifyContent="space-between">
				<Stack>
					<Typography
						sx={{
							color: '#2196f3',
							textAlign: 'left'
						}}
						fontSize={11}
					>
						Parametros:
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{`Sucursal: ${selectedSucursal === 't' ? 'Todas' : sucursalName}`}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{`Gestor Seleccionado: ${selectedGestor === 't' ? 'Todos' : gestorName}`}
					</Typography>

					<Typography fontSize={11} textAlign="left">
						{`Moneda: ${
							parseInt(moneda) === 1 ? 'Cordoba' : 'Dolar'
						} - Tipo de Crédito: ${
							tipoCredito === 't'
								? 'Todos'
								: tipoCredito === '1'
								? 'EMPEÑO'
								: 'CREDITO'
						}`}
					</Typography>
				</Stack>

				<Stack>
					<Typography
						sx={{
							color: '#2196f3',
							textAlign: 'left'
						}}
						fontSize={11}
					>
						Usuario:
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{user}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{rol}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{`Fecha y Hora: ${moment().format('DD/MM/YYYY hh:mm A')}`}
					</Typography>
				</Stack>
			</Stack>
		</Container>
	);

	const DataTable = ({ dataGestor = [], headerFontSize, dataFontSize, isForPrint = false }) => {
		const columns = [
			{
				key: 'gestor',
				label: 'Gestor',
				align: 'left',
				getValue: (r) => r.gestor
			},
			{
				key: 'temprana',
				label: 'Temprana',
				align: 'center',
				getValue: (r) => r.temprana
			},
			{
				key: 'avanzada',
				label: 'Avanzada',
				align: 'center',
				getValue: (r) => r.avanzada
			},
			{
				key: 'riesgo',
				label: 'Riesgo',
				align: 'center',
				getValue: (r) => r.riesgo
			},
			{
				key: 'riesgoPerdido',
				label: 'Riesgo Perdido',
				align: 'center',
				getValue: (r) => r.riesgoPerdido
			},
			{
				key: 'vencido',
				label: 'Vencido',
				align: 'center',
				getValue: (r) => r.vencido
			}
		];

		return (
			<>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								{columns.map((col) => (
									<TableCell
										key={col.key}
										align={col.align}
										style={{ fontSize: headerFontSize }}
										className="text-primary fw-bold"
									>
										{col.label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{dataGestor.map((r) => {
								return (
									<TableRow key={r.gestor}>
										{columns.map((col) => (
											<TableCell
												key={col.key}
												align={col.align}
												style={{ fontSize: dataFontSize }}
												className={isForPrint ? 'text-dark' : ''}
											>
												{col.getValue(r)}
											</TableCell>
										))}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</>
		);
	};

	const consolidatedTotals = useMemo(() => {
		if (!data.length) {
			return {
				totalTemprana: 0,
				totalAvanzada: 0,
				totalRiesgo: 0,
				totalRiesgoPerdido: 0,
				totalVencido: 0
			};
		}

		return data.reduce(
			(acc, c) => {
				acc.totalTemprana += c.temprana;
				acc.totalAvanzada += c.avanzada;
				acc.totalRiesgo += c.riesgo;
				acc.totalRiesgoPerdido += c.riesgoPerdido;
				acc.totalVencido += c.vencido;

				return acc;
			},
			{
				totalTemprana: 0,
				totalAvanzada: 0,
				totalRiesgo: 0,
				totalRiesgoPerdido: 0,
				totalVencido: 0
			}
		);
	}, [data]);

	const { totalTemprana, totalAvanzada, totalRiesgo, totalRiesgoPerdido, totalVencido } =
		consolidatedTotals;

	const footer = (
		<Stack>
			<Typography
				sx={{
					color: '#2196f3',
					textAlign: 'center'
				}}
			>
				Consolidado
			</Typography>

			<hr />

			<Stack direction="row" flex="row" justifyContent="space-around">
				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Temprana
					</Typography>
					<Typography style={{ fontSize: 11 }}>{totalTemprana}</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Avanzada
					</Typography>
					<Typography style={{ fontSize: 11 }}>{totalAvanzada}</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Riesgo
					</Typography>
					<Typography style={{ fontSize: 11 }}>{totalRiesgo}</Typography>
				</Stack>
				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Riesgo Perdido
					</Typography>
					<Typography style={{ fontSize: 11 }}>{totalRiesgoPerdido}</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Vencido
					</Typography>
					<Typography style={{ fontSize: 11 }}>{totalVencido}</Typography>
				</Stack>
			</Stack>
			<hr />
		</Stack>
	);

	return (
		<div>
			<Dialog fullScreen open={true}>
				<AppBar sx={{ position: 'relative' }}>
					<Toolbar>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
						<Typography
							sx={{ ml: 2, flex: 1, textAlign: 'center' }}
							variant="h5"
							component="div"
						>
							{`${title} `}
						</Typography>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
					</Toolbar>
				</AppBar>

				<Stack display="flex" justifyContent="center">
					<Stack
						direction={'row'}
						justifyContent="space-between"
						paddingLeft={5}
						paddingRight={5}
						marginTop={1}
						marginBottom={1}
					>
						<IconButton variant="outlined" onClick={() => exportExcel()}>
							<FontAwesomeIcon
								icon={faCloudArrowDown}
								style={{ fontSize: 40, color: '#00a152', width: 40 }}
							/>
						</IconButton>

						<Stack>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
								variant="h6"
								component="div"
							>
								Reporte de Bosque de Mora
							</Typography>
						</Stack>

						<ReactToPrint
							trigger={() => {
								return (
									<IconButton
										variant="outlined"
										// style={{ position: "fixed", right: 20, top: 75 }}
									>
										<PrintRoundedIcon
											style={{ fontSize: 40, color: '#2979ff', width: 40 }}
										/>
									</IconButton>
								);
							}}
							content={() => compRef.current}
							pageStyle={pageStyle}
						/>
					</Stack>

					{paramText}

					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						<hr />
						{isEmpty(data) ? (
							<NoData />
						) : (
							<>
								<Divider />

								<DataTable
									dataGestor={data}
									headerFontSize={11}
									dataFontSize={10}
								/>

								{gestorList.length === 1 ? null : footer}
							</>
						)}
					</Container>
				</Stack>
			</Dialog>

			<div
				style={{
					display: 'none'
				}}
			>
				<PrintReport
					ref={compRef}
					titulo={'Reporte de Bosque de Mora'}
					fecha={`Fecha: ${moment().format('L')}`}
				>
					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						{paramText}
					</Container>

					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						<hr />
						{isEmpty(data) ? (
							<NoData />
						) : (
							<>
								<Divider />

								<DataTable
									dataGestor={data}
									headerFontSize={11}
									dataFontSize={10}
									isForPrint={true}
								/>

								{gestorList.length === 1 ? null : footer}
							</>
						)}
					</Container>
				</PrintReport>
			</div>
		</div>
	);
};

export default BosqueMora;
